import type { MixedItemTag } from "@memorang/types";
import type { EXAM_STATUS } from "types/tag";

export type ProethicaTestKind = "pretest" | "posttest";

export type ProethicaTestDescriptor = {
	id: string;
	kind: ProethicaTestKind;
	title: string;
	subtitle: string;
	collectionId: number;
	anchorModuleNumber: number;
	numItems: number;
	status: EXAM_STATUS;
	examName: string;
	icon: string;
	typeLabel: string;
};

// Pre/post tests ride alongside the 8 modules as summative PRACTICE_TEST entries.
// collectionId 799/800 are the live create-params used once isLiveSession flips;
// under mock they only key the descriptor lookup.
export const proethicaTestDescriptors: ProethicaTestDescriptor[] = [
	{
		id: "pretest",
		kind: "pretest",
		title: "Pre-Test",
		subtitle: "Check your baseline before you begin.",
		collectionId: 799,
		anchorModuleNumber: 1,
		numItems: 30,
		status: "NOT_STARTED",
		examName: "Pre Test Items",
		icon: "clipboard-text-outline",
		typeLabel: "Pre-Test",
	},
	{
		id: "posttest",
		kind: "posttest",
		title: "Post-Test",
		subtitle: "Measure how far you've come.",
		collectionId: 800,
		anchorModuleNumber: 8,
		numItems: 30,
		status: "NOT_STARTED",
		examName: "Post test items",
		icon: "clipboard-check-outline",
		typeLabel: "Post-Test",
	},
];

const testById: Record<string, ProethicaTestDescriptor> = Object.fromEntries(
	proethicaTestDescriptors.map((test) => [test.id, test]),
);

export const getProethicaTestDescriptor = (
	id?: string,
): ProethicaTestDescriptor | undefined => (id ? testById[id] : undefined);

export const isProethicaTestId = (id?: string): boolean =>
	id != null && id in testById;

export const getProethicaModuleNumber = (
	tag: MixedItemTag,
): number | undefined => {
	const match = (tag.label || tag.title || "").match(/Module\s+(\d+)/i);
	return match ? Number(match[1]) : undefined;
};

const toTestTag = (test: ProethicaTestDescriptor): MixedItemTag => ({
	id: test.id,
	title: test.title,
	label: test.title,
	type: "Practice Test",
	displayType: ["Display"],
	distribution: { high: 0, low: 0, medium: 0, none: test.numItems },
	numItems: test.numItems,
	locked: false,
});

// Single ordering rule shared by every dashboard surface: each test row follows
// its anchor module wherever that module lands in the list. Test descriptors
// never match the "Module N" regex, so they cannot trigger their own injection.
export const injectProethicaTests = (tags: MixedItemTag[]): MixedItemTag[] => {
	const result: MixedItemTag[] = [];
	for (const tag of tags) {
		result.push(tag);
		const moduleNumber = getProethicaModuleNumber(tag);
		const test = proethicaTestDescriptors.find(
			(descriptor) => descriptor.anchorModuleNumber === moduleNumber,
		);
		if (test) {
			result.push(toTestTag(test));
		}
	}
	return result;
};
